import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import SwiperCore from 'swiper';

SwiperCore.use([FreeMode, Autoplay, Navigation]);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

const CaseStudiesCarousel = ({ 
  title = "Latest Case Studies", 
  limit = 6, 
  showNavButtons = true,
  className = ""
}) => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    fetchCaseStudies();
  }, [limit]);

  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/case-studies?limit=${limit}&status=published&featured=true`);
      if (!response.ok) {
        throw new Error('Failed to fetch case studies');
      }
      const data = await response.json();
      setCaseStudies(data.caseStudies || []);
    } catch (error) {
      console.error('Error fetching case studies:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <section className={`container mx-auto px-4 py-16 ${className}`}>
        <h2 className="text-4xl font-bold text-center mb-12">
          <span className="text-[#1976f2]">{title}</span>
        </h2>
        <div className="flex justify-center items-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1976f2]"></div>
        </div>
      </section>
    );
  }

  if (error || caseStudies.length === 0) {
    return (
      <section className={`container mx-auto px-4 py-16 ${className}`}>
        <h2 className="text-4xl font-bold text-center mb-12">
          <span className="text-[#1976f2]">{title}</span>
        </h2>
        <div className="text-center text-gray-600">
          {error ? `Error: ${error}` : 'No case studies available at the moment.'}
        </div>
      </section>
    );
  }

  return (
    <section className={`container mx-auto px-4 py-16 ${className}`}>
      <h2 className="text-4xl font-bold text-center mb-12">
        <span className="text-[#1976f2]">{title}</span>
      </h2>
      <div className="overflow-hidden relative">
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          spaceBetween={16}
          loop={true}
          freeMode={true}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          speed={1000}
          grabCursor={true}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          breakpoints={{
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
          }}
          className="w-full case-studies-swiper"
        >
          {caseStudies.map((caseStudy, index) => (
            <SwiperSlide key={`${caseStudy._id}-${index}`}> 
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={caseStudy.featuredImage} 
                    alt={caseStudy.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = "https://dummyimage.com/400x300/eeeeee";
                    }}
                  />
                </div>
                <div className="p-6">
                  <div className="mb-3 flex items-center justify-between text-sm text-gray-500">
                    <span className="bg-[#1976f2] text-white px-2 py-1 rounded text-xs">
                      {caseStudy.category}
                    </span>
                    <span>{formatDate(caseStudy.publishedAt || caseStudy.createdAt)}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 leading-tight hover:text-[#1976f2] transition-colors">
                    <Link to={`/case-study/${caseStudy.slug}`} onClick={() => window.scrollTo(0, 0)}>
                      {caseStudy.title}
                    </Link>
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {caseStudy.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <Link 
                      to={`/case-study/${caseStudy.slug}`}
                      onClick={() => window.scrollTo(0, 0)}
                      className="text-[#1976f2] hover:text-blue-700 font-medium inline-flex items-center transition-colors"
                    >
                      Read More <span className="ml-1">→</span>
                    </Link>
                    <span className="text-xs text-gray-400">
                      {caseStudy.readTime} min read
                    </span>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {/* Custom Navigation Buttons - match BlogsCarousel */}
        <div className="flex justify-center gap-4 mt-10">
          <button
            onClick={() => swiperRef.current?.swiper.slidePrev()}
            aria-label="Previous"
            className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => swiperRef.current?.swiper.slideNext()}
            aria-label="Next"
            className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      {/* Add custom CSS for Swiper navigation buttons in your global CSS or this component's CSS module */}
    </section>
  );
};

export default CaseStudiesCarousel;
